import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Shield, FileText, Package, ExternalLink, ShoppingBag } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { sectors } from '../data/sectors';
import { products } from '../data/products';
import { ProductPlaceholder } from '../components/ProductPlaceholder';
import { SquareCard } from '../components/SquareCard';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { getShopSectorUrl, getShopProductUrl, getShopCollectionUrl } from '../data/shopUrls';
export function SectorPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const sector = sectors.find(s => s.slug === slug);
  if (!sector) {
    return <Navigate to="/sectors" replace data-id="element-1246" />;
  }
  // Filter products for this sector
  const relevantProducts = products.filter(p => p.sectors.includes(sector.slug));
  return <>
      <PageHero label="Sector Profile" title={sector.name} subtitle={sector.shortDescription} data-id="element-1247" />

      <Breadcrumbs items={[{
      label: 'Manufacturing',
      to: '/manufacturing'
    }, {
      label: 'Our Markets',
      to: '/manufacturing/markets'
    }, {
      label: sector.name
    }]} data-id="element-1248" />

      {/* Browse Products Module - NEW */}
      <section className="bg-white py-12 border-b border-steel-100" data-id="element-1249">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1250">
          <div className="bg-navy-900 rounded-sm p-8 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-1251">
            <div data-id="element-1252">
              <span className="text-xs font-bold text-cg-orange-600 uppercase tracking-widest mb-2 block" data-id="element-1253">
                Product Catalogue
              </span>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-1254">
                Browse {sector.name} Equipment
              </h3>
              <p className="text-steel-300 text-sm" data-id="element-1255">
                View the full product catalogue filtered for{' '}
                {sector.name.toLowerCase()} applications.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0" data-id="element-1256">
              <a href={getShopSectorUrl(sector.slug)} target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-cg-orange-600 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-cg-orange-500 transition-colors whitespace-nowrap text-center flex items-center justify-center gap-2" data-id="element-1257">
                <ShoppingBag className="w-4 h-4" data-id="element-1258" /> Browse {sector.name}{' '}
                Inventory
              </a>
              <Link to="/contact" className="px-6 py-3 border border-white/30 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-white/10 transition-colors whitespace-nowrap text-center" data-id="element-1259">
                Discuss a Requirement
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sector Overview & Use Cases - ENHANCED */}
      <section className="bg-white py-24" data-id="element-1260">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1261">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16" data-id="element-1262">
            <div className="lg:col-span-2" data-id="element-1263">
              <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-1264">
                Sector Overview
              </h2>
              <div className="prose prose-lg text-steel-600 max-w-none mb-10" data-id="element-1265">
                {sector.overview.split('\n\n').map((paragraph, i) => <p key={i} className="mb-6 leading-relaxed" data-id="element-1266">
                    {paragraph}
                  </p>)}
              </div>

              <div className="bg-slate-50 border-l-4 border-navy-900 p-8 rounded-sm" data-id="element-1267">
                <h3 className="text-lg font-bold text-navy-900 mb-4" data-id="element-1268">
                  Common Use Cases & Deployments
                </h3>
                <p className="text-steel-600 leading-relaxed" data-id="element-1269">
                  {sector.useCases}
                </p>
              </div>
            </div>

            {/* Procurement Requirements Panel */}
            <div className="lg:col-span-1 space-y-8" data-id="element-1270">
              <div className="bg-white border-l-4 border-cg-orange-600 border border-steel-200 p-8 shadow-none" data-id="element-1271">
                <h3 className="text-lg font-bold text-navy-900 mb-6 flex items-center gap-2" data-id="element-1272">
                  <Shield className="w-5 h-5 text-cg-orange-600" data-id="element-1273" /> Procurement
                  Requirements
                </h3>
                <ul className="space-y-4" data-id="element-1274">
                  {sector.procurementRequirements.map((req, i) => <li key={i} className="flex items-start gap-3" data-id="element-1275">
                      <CheckCircle2 className="w-5 h-5 text-cg-orange-600 flex-shrink-0 mt-0.5" data-id="element-1276" />
                      <span className="text-sm text-steel-700 font-medium" data-id="element-1277">
                        {req}
                      </span>
                    </li>)}
                </ul>
              </div>

              {/* Documentation Note - NEW */}
              <div className="bg-navy-900 p-8 rounded-sm text-white" data-id="element-1278">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2" data-id="element-1279">
                  <FileText className="w-5 h-5 text-cg-orange-600" data-id="element-1280" />{' '}
                  Documentation
                </h3>
                <p className="text-sm text-steel-300 leading-relaxed mb-4" data-id="element-1281">
                  {sector.documentationNote}
                </p>
                <Link to="/contact" className="text-xs font-bold text-cg-orange-600 uppercase tracking-wider hover:text-white transition-colors" data-id="element-1282">
                  Request Documentation &rarr;
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Typical Procurement Categories - Square Card Layout */}
      <section className="bg-slate-50 py-24 border-t border-steel-200" data-id="element-1283">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1284">
          <h2 className="text-2xl font-bold text-navy-900 mb-10" data-id="element-1285">
            Typical Procurement Categories
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1286">
            {sector.typicalCategories.map(cat => <SquareCard key={cat.slug} to={getShopCollectionUrl(cat.slug)} isExternal={true} title={cat.name} actionText="Browse Inventory" variant="category" data-id="element-1287" />)}
          </div>
        </div>
      </section>

      {/* Recommended Products */}
      <section className="bg-white py-24 border-t border-steel-200" data-id="element-1288">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1289">
          <div className="flex justify-between items-end mb-10" data-id="element-1290">
            <div data-id="element-1291">
              <h2 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-1292">
                Commonly Procured Items
              </h2>
              <p className="text-steel-600" data-id="element-1293">
                Standard equipment configurations for{' '}
                {sector.name.toLowerCase()} applications.
              </p>
            </div>
            <Link to="/products" className="text-sm font-bold text-cg-orange-600 uppercase tracking-wider flex items-center gap-2" data-id="element-1294">
              View Full Catalogue <ArrowRight className="w-4 h-4" data-id="element-1295" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1296">
            {relevantProducts.slice(0, 4).map(product => <a key={product.slug} href={getShopProductUrl(product.slug)} target="_blank" rel="noopener noreferrer" className="group bg-white border border-steel-200 hover:border-cg-orange-600 transition-colors" data-id="element-1297">
                <div className="aspect-square bg-slate-100 overflow-hidden relative" data-id="element-1298">
                  <ProductPlaceholder aspectRatio="1/1" className="w-full h-full" data-id="element-1299" />
                  <div className="absolute top-3 left-3" data-id="element-1300">
                    <span className="bg-navy-900/90 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm" data-id="element-1301">
                      {product.categoryName}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3" data-id="element-1302">
                    <span className="bg-cg-orange-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm shadow-sm" data-id="element-1303">
                      Inventory
                    </span>
                  </div>
                </div>
                <div className="p-5" data-id="element-1304">
                  <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-cg-orange-600 transition-colors line-clamp-1" data-id="element-1305">
                    {product.name}
                  </h3>
                  <p className="text-xs text-steel-600 mb-4 line-clamp-2" data-id="element-1306">
                    {product.shortDescription}
                  </p>
                  <span className="text-xs font-bold text-navy-900 uppercase tracking-wider flex items-center gap-2" data-id="element-1307">
                    View in Inventory{' '}
                    <ExternalLink className="w-3 h-3 group-hover:translate-x-1 transition-transform" data-id="element-1308" />
                  </span>
                </div>
              </a>)}
          </div>
        </div>
      </section>

      {/* Framework Relevance - ENHANCED */}
      <section className="bg-navy-900 py-20" data-id="element-1309">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1310">
          <div className="bg-navy-800 border border-white/5 p-12 rounded-sm flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-1311">
            <div data-id="element-1312">
              <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1313">
                Procurement via Approved Frameworks
              </h2>
              <p className="text-steel-400 max-w-xl" data-id="element-1314">{sector.frameworkNote}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0" data-id="element-1315">
              <Link to="/frameworks" className="px-8 py-4 bg-white text-navy-900 hover:bg-steel-100 font-bold uppercase tracking-wider text-sm rounded-sm transition-colors whitespace-nowrap text-center" data-id="element-1316">
                View Frameworks
              </Link>
              <Link to="/frameworks#request-framework-details" className="px-8 py-4 border border-white/30 text-white hover:bg-white/10 font-bold uppercase tracking-wider text-sm rounded-sm transition-colors whitespace-nowrap text-center" data-id="element-1317">
                Request Framework Details
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-24 border-t border-steel-100" data-id="element-1318">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center" data-id="element-1319">
          <h2 className="text-3xl font-bold text-navy-900 mb-6" data-id="element-1320">
            Discuss a {sector.name} Requirement
          </h2>
          <p className="text-steel-600 max-w-2xl mx-auto mb-10 text-lg" data-id="element-1321">
            Contact our specialist team to discuss specifications, volume
            requirements, and lead times for {sector.name.toLowerCase()}{' '}
            equipment.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center px-10 py-4 bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm transition-colors" data-id="element-1322">
            Contact Procurement Team
          </Link>
        </div>
      </section>
    </>;
}